import React, { createContext, useContext, useState } from "react";
import Toast from "./index";
type ToastType = "success" | "error" | "info" | "warning" | "loading";

type ToastData = {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
};

type ToastContextType = {
  toast: (message: string, type?: ToastType, duration?: number) => string;
  dismiss: (id: string) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};


const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const toast = (message: string, type: ToastType = "info", duration: number = 3000): string => {
    const id = Math.random().toString(36).substring(2, 9); // Generate a unique ID
    setToasts((prevToasts) => [...prevToasts, { id, message, type, duration }]);
    return id;
  };

  const dismiss = (id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      <div style={styles.toastContainer}>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => dismiss(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  toastContainer: {
    position: "fixed",
    top: "20px",
    right: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    zIndex: 1000,
  },
};

export default ToastProvider;
